import { ulid } from 'ulid';

import {
	ConditionOperator as ConditionOperatorEnum,
	type CreateScenarioInput,
	type UpdateScenarioInput
} from '@/graphql/generated';

export interface ScenarioConditionFormState {
	conditionId?: string;
	description?: string;
	startDate?: string;
	endDate?: string;
	arrivingSince?: string;
	arrivingUntil?: string;
	leavingSince?: string;
	leavingUntil?: string;
	weeklyDateSetId?: number;
	weeklyDateSetName?: string;
	appliesOnHolidays: boolean;
	minStayMinutes?: number;
	maxStayMinutes?: number;
	appliesToSpecificVehicleTypes: boolean;
	appliesToSpecificCustomerCategories: boolean;
	appliesToSpecificPaymentMethods: boolean;
	isOverstay: boolean;
	isContractOverstay: boolean;
	selectedVehicleTypes: string[];
	selectedCustomerCategories: string[];
	selectedPaymentMethods: string[];
	selectedDaysOfWeek: string[];
	operator: 'include' | 'exclude';
}

export interface ScenarioCreateFormState {
	name: string;
	conditions: ScenarioConditionFormState[];
}

const toUtcDateTime = (value?: string): string | undefined => {
	if (!value) {
		return undefined;
	}

	return `${value}T00:00:00Z`;
};

const toLocalTime = (value?: string): string | undefined => {
	if (!value) {
		return undefined;
	}

	return value.length === 5 ? `${value}:00` : value;
};

export const buildCreateScenarioInput = (
	formState: ScenarioCreateFormState,
	scenarioId: string
): CreateScenarioInput => {
	if (!formState.name?.trim()) {
		throw new Error('Name is required to create a scenario.');
	}

	if (!formState.conditions.length) {
		throw new Error('Add at least one rule before creating a scenario.');
	}

	const scenarioConditions = formState.conditions.map((condition, index) => {
		if (condition.weeklyDateSetId == null) {
			throw new Error(`Rule #${index + 1} requires a weekly date set.`);
		}

		// No validation needed - flags are automatically set based on selections

		const startDate = toUtcDateTime(condition.startDate);
		const endDate = toUtcDateTime(condition.endDate);
		const arrivingSince = toLocalTime(condition.arrivingSince);
		const arrivingUntil = toLocalTime(condition.arrivingUntil);
		const leavingSince = toLocalTime(condition.leavingSince);
		const leavingUntil = toLocalTime(condition.leavingUntil);

		return {
			conditionId: condition.conditionId ?? ulid(),
			operator:
				condition.operator === 'exclude'
					? ConditionOperatorEnum.Exclude
					: ConditionOperatorEnum.Include,
			condition: {
				weeklyDateSetId: condition.weeklyDateSetId,
				appliesOnHolidays: Boolean(condition.appliesOnHolidays),
				appliesToSpecificVehicleTypes: Boolean(
					condition.appliesToSpecificVehicleTypes
				),
				appliesToSpecificCustomerCategories: Boolean(
					condition.appliesToSpecificCustomerCategories
				),
				appliesToSpecificPaymentMethods: Boolean(
					condition.appliesToSpecificPaymentMethods
				),
				isOverstay: Boolean(condition.isOverstay),
				isContractOverstay: Boolean(condition.isContractOverstay),
				conditionDayOfWeeks: condition.selectedDaysOfWeek.map(value =>
					Number(value)
				),
				conditionVehicleTypes: condition.appliesToSpecificVehicleTypes
					? condition.selectedVehicleTypes.map(value => Number(value))
					: [],
				conditionCustomerCategories:
					condition.appliesToSpecificCustomerCategories
						? condition.selectedCustomerCategories
						: [],
				conditionPaymentMethods:
					condition.appliesToSpecificPaymentMethods
						? condition.selectedPaymentMethods.map(value =>
								Number(value)
							)
						: [],
				...(startDate ? { startDate } : {}),
				...(endDate ? { endDate } : {}),
				...(arrivingSince ? { arrivingSince } : {}),
				...(arrivingUntil ? { arrivingUntil } : {}),
				...(leavingSince ? { leavingSince } : {}),
				...(leavingUntil ? { leavingUntil } : {}),
				...(condition.minStayMinutes != null
					? { minStayMinutes: condition.minStayMinutes }
					: {}),
				...(condition.maxStayMinutes != null
					? { maxStayMinutes: condition.maxStayMinutes }
					: {})
			}
		};
	});

	return {
		scenarioId,
		name: formState.name.trim(),
		scenarioConditions
	};
};

export const buildUpdateScenarioInput = (
	formState: ScenarioCreateFormState,
	scenarioId: string
): UpdateScenarioInput => {
	if (!formState.name?.trim()) {
		throw new Error('Name is required to update a scenario.');
	}

	if (!formState.conditions.length) {
		throw new Error('Add at least one rule before updating a scenario.');
	}

	const scenarioConditions = formState.conditions.map((condition, index) => {
		if (condition.weeklyDateSetId == null) {
			throw new Error(`Rule #${index + 1} requires a weekly date set.`);
		}

		const startDate = toUtcDateTime(condition.startDate);
		const endDate = toUtcDateTime(condition.endDate);
		const arrivingSince = toLocalTime(condition.arrivingSince);
		const arrivingUntil = toLocalTime(condition.arrivingUntil);
		const leavingSince = toLocalTime(condition.leavingSince);
		const leavingUntil = toLocalTime(condition.leavingUntil);

		return {
			conditionId: condition.conditionId ?? ulid(),
			operator:
				condition.operator === 'exclude'
					? ConditionOperatorEnum.Exclude
					: ConditionOperatorEnum.Include,
			condition: {
				// Required field for UpdateConditionInput
				description: condition.description || 'Generated condition',
				weeklyDateSetId: condition.weeklyDateSetId,
				appliesOnHolidays: Boolean(condition.appliesOnHolidays),
				appliesToSpecificVehicleTypes: Boolean(
					condition.appliesToSpecificVehicleTypes
				),
				appliesToSpecificCustomerCategories: Boolean(
					condition.appliesToSpecificCustomerCategories
				),
				isOverstay: Boolean(condition.isOverstay),
				isContractOverstay: Boolean(condition.isContractOverstay),
				conditionDayOfWeeks: condition.selectedDaysOfWeek.map(value =>
					Number(value)
				),
				conditionVehicleTypes: condition.appliesToSpecificVehicleTypes
					? condition.selectedVehicleTypes.map(value => Number(value))
					: [],
				conditionCustomerCategories:
					condition.appliesToSpecificCustomerCategories
						? condition.selectedCustomerCategories
						: [],
				...(startDate ? { startDate } : {}),
				...(endDate ? { endDate } : {}),
				...(arrivingSince ? { arrivingSince } : {}),
				...(arrivingUntil ? { arrivingUntil } : {}),
				...(leavingSince ? { leavingSince } : {}),
				...(leavingUntil ? { leavingUntil } : {}),
				...(condition.minStayMinutes != null
					? { minStayMinutes: condition.minStayMinutes }
					: {}),
				...(condition.maxStayMinutes != null
					? { maxStayMinutes: condition.maxStayMinutes }
					: {})
			}
		};
	});

	return {
		scenarioId,
		name: formState.name.trim(),
		scenarioConditions
	};
};
